import { useState } from 'react';

import { cn, ia, iaRa, io, pluralize } from 'lib/helpers/utility';

import Badge from 'components/shared/Badge/Badge';
import Button from 'components/shared/Button/Button';
import DateRangeSelector from 'components/shared/DateRangeSelector/DateRangeSelector';

import {
  getActiveFilters,
  getFormulaLabel,
  getFormulaOptions,
  signatureStatusOptions,
  textOptions
} from '../lib/helpers';

import TextInput from './TextInput';
import RangeSelector from './RangeSelector';
import InfiniteDropdown from './InfiniteDropdown';

const rangeTypes = ['range', 'number', 'number-range'];

const AppliedFilters = ({
  filters = {},
  defaultFilters = {},
  changeFilters,
  updateFilters,
  visibleCount,
  position = 'right',
  hideOperator = false,
  // Opt-in map of filterKey -> render fn for the chip's value segment. When provided for a
  // key, it replaces the default dropdown/input there. Default {} keeps every other
  // consumer unchanged.
  valueRenderers = {},
  // Opt-in "name-only until selected" chips: a chip with no value renders as just its name
  // (the value segment is the trigger); once a value is picked it shows "name value ✕" where
  // ✕ clears the value (keeping the always-visible chip) instead of removing it. Default
  // false keeps every other consumer unchanged.
  clearable = false
}) => {
  const [count, setCount] = useState();

  const appliedFilters = getActiveFilters(filters).filter(([key]) => key !== 'search');

  if (!ia(appliedFilters)) return null;

  const visibleFilters = count ? appliedFilters.slice(0, count) : appliedFilters;

  const handleShowMore = () => {
    if (!visibleCount) return;
    setCount((prev) => (prev === undefined ? visibleCount : undefined));
  };

  return (
    <>
      {visibleFilters.map(([key, filter]) => {
        return iaRa(filter).map((_filter, index) => {
          const { title, titleClassName, icon, values, formula, type } = _filter;
          const pluralLabel = ia(values) && pluralize(title || '', values.length, ['uploaded by']);

          const [item] = (ia(values) && values) || [];

          const handleRemoveFilter = () => {
            changeFilters((prev) => {
              if (!ia(prev[key])) return prev;
              const filteredArray = prev[key].filter((f, filterIndex) => filterIndex !== index);
              if (!ia(filteredArray)) {
                const { [key]: _, ...rest } = prev;
                return rest;
              }
              return {
                ...prev,
                [key]: filteredArray
              };
            });
          };

          // clearable: keep the (always-visible) chip but reset its value to empty so it
          // falls back to the name-only state, instead of removing the chip entirely.
          const handleClearValue = () => {
            changeFilters((prev) => ({
              ...prev,
              [key]: iaRa(prev[key]).map((f, filterIndex) =>
                filterIndex === index ? { ...f, values: [] } : f
              )
            }));
          };

          const handleFormulaChange = (formula) => {
            changeFilters((prev) => ({
              ...prev,
              [key]: prev[key].map((f, filterIndex) =>
                filterIndex === index ? { ...f, formula: formula.value } : f
              )
            }));
          };

          const handleOutsideClick = (items) => {
            if (!ia(items) && !io(items) && typeof items !== 'number') {
              // Deselecting everything clears the value (clearable) or drops the chip.
              if (clearable) handleClearValue();
              else handleRemoveFilter();
              return;
            }

            changeFilters((prev) => ({
              ...prev,
              [key]: prev[key].map((_filter, filterIndex) => {
                if (filterIndex !== index) return _filter;
                const activeFormula = formula || filters[key]?.formula;
                return {
                  ..._filter,
                  values: items,
                  formula: activeFormula || 'is',
                  ...(ia(items) &&
                    activeFormula !== 'is not' &&
                    !signatureStatusOptions.includes(activeFormula) && {
                      formula: items.length > 1 ? 'is any of' : 'is'
                    })
                };
              })
            }));
          };

          const buttonText = !ia(values, 1)
            ? item?.label
            : `${values.length} ${pluralLabel.toLowerCase()}`;

          // clearable mode: chip reads name-only until a value is picked. When empty the
          // value segment itself shows the icon + name (and is the dropdown trigger); once a
          // value exists we show a separate name label + the value + a clear (✕) button.
          const hasValue = ia(values);
          const showNameLabel = !clearable || hasValue;
          const showClear = clearable ? hasValue : !_filter.default;
          const valueIcon = clearable && !hasValue ? icon : undefined;
          const valueText = clearable && !hasValue ? title : buttonText;
          // Empty clearable chip is a standalone pill (round both sides) showing the full name
          // (lift the default value-trigger max-width so long titles aren't truncated);
          // otherwise the value sits between the name and ✕, so square it. Non-clearable
          // consumers keep the prior `_filter.default && rounded-r-md` behavior.
          const valueClassName = clearable
            ? hasValue
              ? 'rounded-none'
              : 'max-w-none rounded-md'
            : undefined;
          const legacyValueRounding = !clearable && _filter.default && 'rounded-r-md';

          return (
            <Button.Group key={key + '-' + index} variant="tertiary" size="xs">
              {showNameLabel && (
                <Button
                  icon={icon}
                  text={title}
                  textClassName={titleClassName}
                  iconColor="neutral"
                  className={cn(
                    'pointer-events-none whitespace-nowrap hover:bg-transparent active:bg-transparent',
                    // With the operator hidden (or in clearable mode) the name sits directly
                    // beside the value; drop its right border so the value segment's left
                    // border is the only divider.
                    (hideOperator || clearable) && 'border-r-0'
                  )}
                />
              )}
              {!hideOperator && (
                <Button.Dropdown
                  items={getFormulaOptions(
                    type,
                    iaRa(values)?.length,
                    values,
                    null,
                    _filter.numeric
                  )}
                  dropdownClassName="rounded-[4px]"
                  onClick={handleFormulaChange}
                  active={formula}
                  text={getFormulaLabel(formula, _filter.numeric)}
                  className="whitespace-nowrap border-x-0"
                  index={index}
                />
              )}
              {valueRenderers[key] ? (
                valueRenderers[key]({
                  values,
                  onChange: handleOutsideClick,
                  data: defaultFilters[key],
                  index,
                  position,
                  hasValue,
                  icon,
                  title,
                  valueClassName
                })
              ) : type === 'date-range' ? (
                <DateRangeSelector
                  value={values}
                  onChange={handleOutsideClick}
                  buttonClassName={cn(
                    'whitespace-nowrap rounded-none',
                    valueClassName,
                    legacyValueRounding
                  )}
                  index={index}
                  position={position}
                  direction={defaultFilters[key]?.direction}
                />
              ) : rangeTypes.includes(type) ? (
                <RangeSelector
                  values={values}
                  onApply={handleOutsideClick}
                  buttonClassName={cn(
                    'whitespace-nowrap rounded-none',
                    valueClassName,
                    legacyValueRounding
                  )}
                  initialOpenState={!values}
                  formula={formula}
                  numeric={_filter.numeric}
                  index={index}
                  position={position}
                />
              ) : type === 'text' && textOptions.includes(formula) ? (
                <TextInput
                  values={values}
                  onApply={handleOutsideClick}
                  formula={formula}
                  initialOpenState={!values}
                  index={index}
                  position={position}
                />
              ) : (
                <InfiniteDropdown
                  items={values}
                  data={defaultFilters[key]}
                  dropdownClassName={cn(
                    'absolute m-0 z-[100] shadow-xs',
                    position === 'left' ? 'right-0' : 'left-0'
                  )}
                  onChange={handleOutsideClick}
                  active={iaRa(values).includes(item?.value)}
                  text={valueText}
                  icon={valueIcon}
                  iconColor="neutral"
                  className={valueClassName}
                  withButton
                  index={index}
                  onWrongFilter={updateFilters}
                />
              )}
              {showClear && (
                <Button
                  icon="close-linear"
                  iconColor="neutral"
                  className="border-l-0"
                  iconShade={700}
                  iconSize={16}
                  onClick={clearable ? handleClearValue : handleRemoveFilter}
                />
              )}
            </Button.Group>
          );
        });
      })}
      {visibleCount && visibleCount < appliedFilters.length && (
        <Badge
          color="neutral"
          size="medium"
          text={
            count < appliedFilters.length ? `+${appliedFilters.length - count} more` : 'Show less'
          }
          onClick={handleShowMore}
          isRounded={false}
        />
      )}
    </>
  );
};

export default AppliedFilters;
